import { OptionInput, PhoneInput, TextInput } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import { useRouter } from "next/router";
import * as React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { useFormSubmissionContext } from "@brexhq/next/src/components/modules/LeadForm/FormSubmissionProvider";
import { SalesPageTemplate } from "@brexhq/next/src/components/modules/LeadForm/Steps/PageTemplates/SalesPageTemplate";
import { LeadFormSteps } from "@brexhq/next/src/components/modules/LeadForm/machines/types";
import {
  FormFields,
  LeadFormValues,
  StepProps,
} from "@brexhq/next/src/components/modules/LeadForm/sharedTypes";
import isPersonalEmail from "@brexhq/next/src/utils/isPersonalEmail";

import { LeadSelfAttestedType } from "../../../../../../types/brex-api";
import { salesRepNameMapper, getRefCodeFromUrl } from "../../helpers/helpers";
import { employeeCountOptionsOBS4 } from "../../helpers/leadFormFields";

export const ExternalEventUserInfo: React.FC<StepProps> = () => {
  const { spacing } = useTheme();
  const {
    submitError,
    submit,
    stateMachineCtx,
    stepTransitionDirection,
    form: {
      getValues,
      setValue,
      fields,
      setShowFinalStateView,
      programName,
      setProgress,
      resetField,
    },
  } = useFormSubmissionContext();
  const router = useRouter();
  const resetAllSubsequentFields = React.useCallback(() => {
    Object.keys(fields)
      .filter(
        (field) =>
          ![
            FormFields.firstName,
            FormFields.lastName,
            FormFields.companyName,
            FormFields.mobileNumber,
            FormFields.workEmail,
            FormFields.employeeCount,
          ].includes(field as FormFields),
      )
      .map((field) => resetField(field as FormFields));
  }, [resetField, fields]);

  React.useEffect(() => {
    setShowFinalStateView(false);
    setProgress(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Checks if fields are valid to disable */
  const validFields =
    !!getValues("firstName") &&
    !!getValues("lastName") &&
    !!getValues("companyName") &&
    !!getValues("mobileNumber") &&
    !!getValues("workEmail") &&
    !!getValues("employeeCount") &&
    !fields.mobileNumber.error &&
    !fields.workEmail.error;

  const onSubmit = React.useCallback(
    async (event) => {
      /**
       * List of fields used for this form. Most will have 1 field.
       * Type safe so the string fieldNames are based off FormValues
       */
      const fieldList: (keyof LeadFormValues)[] = [
        "firstName",
        "lastName",
        "companyName",
        "mobileNumber",
        "workEmail",
        "employeeCount",
        "salesRepName",
      ];

      if (getValues("workEmail") !== stateMachineCtx.workEmail) {
        resetAllSubsequentFields();
      }

      setValue("isEcomm", LeadSelfAttestedType.OTHER);
      const currentPath = router.asPath;
      const salesRefCode = getRefCodeFromUrl(currentPath);
      setValue("salesRepName", salesRepNameMapper[salesRefCode]);
      /**
       * Handles form submission including validations.
       * Reference FormSubmissionProvider.tsx for more context
       */
      submit({
        event,
        fields: fieldList,
      });
    },
    [
      getValues,
      stateMachineCtx.workEmail,
      setValue,
      submit,
      resetAllSubsequentFields,
      router.asPath,
    ],
  );

  return (
    <SalesPageTemplate
      title="Tell us about yourself"
      step={LeadFormSteps.eventUserInfo}
      programName={programName}
      nextAction={{
        label: "Continue",
        loading: false,
        disabled: !validFields,
        onClick: onSubmit,
      }}
      stepTransitionDirection={stepTransitionDirection}
      error={submitError || stateMachineCtx.errorMessage}
    >
      <Container display="flex" gap="sml" direction="column">
        <Container
          width="100%"
          display="flex"
          direction={{
            LRG: "row",
            SML: "column",
          }}
          margin={{
            LRG: [0, 0, "xxsml", 0],
            MED: [0, 0, "xxsml", 0],
            SML: [0, 0, "xxsml", 0],
          }}
          gap="sml"
        >
          <div style={{ flex: 1 }}>
            <TextInput
              inputMode="text"
              size="large"
              autoComplete="given-name"
              {...fields.firstName}
            />
          </div>
          <div style={{ flex: 1 }}>
            <TextInput
              inputMode="text"
              size="large"
              autoComplete="family-name"
              {...fields.lastName}
            />
          </div>
        </Container>
        <div style={{ marginBottom: spacing.SML.xxsml }}>
          <TextInput
            size="large"
            autoComplete="organization"
            {...fields.companyName}
          />
        </div>
        <div style={{ marginBottom: spacing.SML.xxsml }}>
          <TextInput
            inputMode="email"
            size="large"
            autoComplete="email"
            {...fields.workEmail}
            warning={
              isPersonalEmail(getValues("workEmail") || "")
                ? "Please use a work email address if available"
                : null
            }
          />
        </div>
        <PhoneInput size="large" autoComplete="tel" {...fields.mobileNumber} />
        <div style={{ marginTop: spacing.SML.xxsml }}>
          <OptionInput
            {...fields.employeeCount}
            label="Company size"
            size="large"
            options={employeeCountOptionsOBS4}
          />
        </div>
      </Container>
    </SalesPageTemplate>
  );
};
